import type { ProductDependenciesConfig } from "../../../types/productDependencies";
import type { LineEdit, LineEdits } from "../types";
import type { MaterialRowItem } from "./materialLineItems";
import {
    buildCategoryEditKeys,
    resolveProductIdInOptions,
} from "./buildCategoryEditKeys";
import { evaluateFormula } from "./evaluateFormula";

type ApplyAttributeDefaultsParams = {
    attributes: Record<string, unknown>;
    materialsByGroup: Record<string, MaterialRowItem[]>;
    dependencies?: ProductDependenciesConfig;
    materialsEdits: LineEdits;
    onlyIfEmpty?: boolean;
};

function normalizeAttributeValue(value: unknown): string | null {
    if (value == null || value === "") return null;
    return String(value).trim();
}

function attributeMatches(
    attributes: Record<string, unknown>,
    attributeName: string,
    attributeValue?: string,
): boolean {
    if (!attributeValue) return false;
    const actual = normalizeAttributeValue(attributes[attributeName]);
    const expected = normalizeAttributeValue(attributeValue);
    return actual != null && expected != null && actual === expected;
}

function defaultMatches(
    attributes: Record<string, unknown>,
    def: {
        attributeName: string;
        attributeValue?: string;
        formulaCondition?: string;
    },
): boolean {
    if (def.formulaCondition?.trim()) {
        return evaluateFormula(def.formulaCondition, attributes) > 0;
    }

    return attributeMatches(attributes, def.attributeName, def.attributeValue);
}

export function applyAttributeDefaults({
    attributes,
    materialsByGroup,
    dependencies,
    materialsEdits,
    onlyIfEmpty = true,
}: ApplyAttributeDefaultsParams): Record<string, Partial<LineEdit>> {
    const patches: Record<string, Partial<LineEdit>> = {};
    if (!dependencies) return patches;

    const { editKeyByCategory, optionsByEditKey } =
        buildCategoryEditKeys(materialsByGroup);

    for (const rule of dependencies.rules) {
        const defaults = rule.attributeDefaults ?? [];
        if (!defaults.length) continue;

        const editKey = editKeyByCategory[rule.category];
        if (!editKey) continue;

        if (onlyIfEmpty && materialsEdits[editKey]?.selectedProductId) {
            continue;
        }

        const options = optionsByEditKey[editKey] ?? [];
        for (const def of defaults) {
            if (!defaultMatches(attributes, def)) {
                continue;
            }

            const resolvedId = resolveProductIdInOptions(
                def.manufacturerProductId,
                options,
            );
            if (resolvedId) {
                patches[editKey] = { selectedProductId: resolvedId };
                break;
            }
        }
    }

    return patches;
}
